import React, { ReactNode } from 'react'
import { useTheme } from '~/common/theme'
import MuiDialog from '@mui/material/Dialog'
import MuiDialogContent from '@mui/material/DialogContent'
import MuiDialogTitle from '@mui/material/DialogTitle'
import styled from 'styled-components'

import Icon from '~/views/components/ui/Icon'
import IconButton from '~/views/components/ui/IconButton'
import Text from '~/views/components/ui/Text'
import { IconNames } from '~/views/components/ui/Icon/Icons'

export interface BaseDialogProps {
  onClose?(): void

  open: boolean
  subtitle?: string | ReactNode
  title: string | ReactNode
  className?: string
  showLine?: boolean
  closeSize?: string
  disableBackdropClick?: boolean
  tight?: boolean
  exitIconPosition?: 'left' | 'right'
  closeIcon?: IconNames
  children?: ReactNode
}

const BaseDialog: React.FC<BaseDialogProps> = ({
  children,
  onClose,
  open,
  subtitle,
  title,
  className,
  showLine = true,
  closeSize,
  disableBackdropClick = false,
  tight = false,
  exitIconPosition,
  closeIcon = 'close',
}) => {
  const theme = useTheme()

  const handleOnClose = (
    _: unknown,
    reason: 'backdropClick' | 'escapeKeyDown',
  ) => {
    if (disableBackdropClick && reason === 'backdropClick') return false
    if (!onClose) return false

    onClose()
    return true
  }

  return (
    <Dialog
      className={className}
      PaperProps={{}}
      open={open}
      onClose={handleOnClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      data-testid="alert-dialog"
      disableEnforceFocus
      tight={tight.toString()}
    >
      <DialogTitle className={className}>
        <Text size="xl">
          {title}{' '}
          {subtitle && (
            <Text fontStyle="italic" color="gray450">
              {subtitle}
            </Text>
          )}
        </Text>
        <IconButton
          onClick={onClose}
          position={exitIconPosition}
          data-testid="closeModalButton"
          size="large"
        >
          <Icon name={closeIcon} width={closeSize || theme.spacing(2)} />
        </IconButton>
      </DialogTitle>
      {showLine && <Line />}
      <DialogContent className={className}>{children}</DialogContent>
    </Dialog>
  )
}

type StyledDialogProps = React.ComponentProps<typeof MuiDialog> & {
  tight?: string
}

const Dialog = styled((props: StyledDialogProps) => (
  <MuiDialog {...props} classes={{ root: 'root', paper: 'paper' }} />
))`
  ${(props) =>
    !props.tight &&
    `
    & .paper {
      max-height: 100vh;
      min-width: 95vh;
      min-height: 60vh;
    }`}
  & .root {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  & .content-root {
    padding: 0;
  }
`

const DialogContent = styled(
  (props: React.ComponentProps<typeof MuiDialogContent>) => (
    <MuiDialogContent {...props} classes={{ root: 'content-root' }} />
  ),
)`
  & .content-root {
    padding: 0;
    overflow: hidden;
  }
`

const DialogTitle = styled(
  (props: React.ComponentProps<typeof MuiDialogTitle>) => (
    <MuiDialogTitle {...props} classes={{ root: 'title-root' }} />
  ),
)(
  ({ theme }) => `
  & .title-root {
    padding: ${theme.spacing(3, 5, 3, 5)};
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
`,
)

const Line = styled.div(
  ({ theme }) => `
  border-bottom: 1px solid ${theme.palette.borders.altoGray};
`,
)

export default BaseDialog
